import React, { useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Stack,
  Typography,
  IconButton,
  Paper,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { ChartBlockView } from "./ChartBlockView";
import { SlideBlockView } from "./SlideBlockView";
import {
  METRIC_OPTIONS,
  CHART_KIND_OPTIONS,
  GRANULARITY_OPTIONS,
  WINDOW_OPTIONS,
} from "../utils/metricOptions";
import type {
  ReportBlock,
  ChartBlock,
  HeadingBlock,
  NarrativeBlock,
} from "../api/types";

/** Block types produced by the generator that carry an editable title. */
const TITLED_GENERATED = new Set([
  "cover",
  "execSummary",
  "breakdown",
  "kpiCards",
  "aiPanel",
  "actionPlan",
  "correlation",
  "scatter",
  "weatherImpact",
]);

/** Generated block types that carry an editable key message under the data. */
const KEY_MESSAGE_TYPES = new Set(["execSummary", "breakdown"]);

/**
 * Metrics the comparison endpoint supports (used by breakdown / scatter /
 * correlation blocks). Ids match comparison.ts's SUPPORTED_METRICS.
 */
const COMPARISON_METRIC_OPTIONS: { value: string; label: string }[] = [
  { value: "sleep_score", label: "Sleep performance" },
  { value: "recovery_score", label: "Recovery" },
  { value: "strain", label: "Strain" },
  { value: "resting_heart_rate", label: "Resting heart rate" },
  { value: "hrv", label: "HRV" },
  { value: "overall_stress", label: "Overall stress" },
  { value: "sleep_duration", label: "Sleep duration" },
  { value: "respiratory_rate", label: "Respiratory rate" },
  { value: "high_sleep_stress", label: "High sleep stress" },
];

/** Segment dimensions a breakdown/correlation/scatter can group by. */
const DIMENSION_OPTIONS: { value: string; label: string }[] = [
  { value: "gender", label: "Gender" },
  { value: "age_group", label: "Age group" },
  { value: "department", label: "Department / role" },
  { value: "shift_pattern", label: "Shift pattern" },
];

interface BlockEditorProps {
  block: ReportBlock;
  clientId: string | null;
  onChange: (block: ReportBlock) => void;
  onRemove: () => void;
  /** Drag handle props from dnd-kit's useSortable listeners/attributes. */
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
  /**
   * Generates Director-targeted narrative from the whole report (server reads
   * the saved layout). Provided only for narrative blocks; returns the text to
   * drop into this block. Undefined when generation isn't available.
   */
  onGenerateNarrative?: () => Promise<string>;
  /**
   * Generates an AI action plan (list of steps) for the whole report. Provided
   * only for action-plan blocks; returns the items to store on the block.
   */
  onGenerateActionPlan?: () => Promise<string[]>;
}

/**
 * Edit surface for one block. Chart blocks show config controls plus a live
 * preview; heading/narrative blocks are inline text; pageBreak is a divider.
 */
export const BlockEditor: React.FC<BlockEditorProps> = ({
  block,
  clientId,
  onChange,
  onRemove,
  dragHandleProps,
  onGenerateNarrative,
  onGenerateActionPlan,
}) => {
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(false);
  const header = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mb: 1 }}
    >
      <Box
        {...dragHandleProps}
        sx={{ cursor: "grab", display: "flex", alignItems: "center", color: "text.disabled" }}
      >
        <DragIndicatorIcon fontSize="small" />
        <Typography variant="caption" sx={{ ml: 0.5, textTransform: "capitalize" }}>
          {block.type === "pageBreak" ? "Page break" : block.type}
        </Typography>
      </Box>
      <Tooltip title="Remove block">
        <IconButton size="small" onClick={onRemove}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const renderBody = () => {
    switch (block.type) {
      case "heading": {
        const b = block as HeadingBlock;
        return (
          <TextField
            fullWidth
            variant="standard"
            placeholder="Section heading"
            value={b.text}
            onChange={(e) => onChange({ ...b, text: e.target.value })}
            slotProps={{ input: { style: { fontSize: 24, fontWeight: 600 } } }}
          />
        );
      }
      case "narrative": {
        const b = block as NarrativeBlock;
        const handleGenerate = async () => {
          if (!onGenerateNarrative) return;
          setGenerating(true);
          setGenError(false);
          try {
            const text = await onGenerateNarrative();
            onChange({ ...b, text });
          } catch {
            setGenError(true);
          } finally {
            setGenerating(false);
          }
        };
        return (
          <Stack spacing={1}>
            {onGenerateNarrative && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip title="Draft an executive narrative for a Director from this report's charts and copy">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        generating ? (
                          <CircularProgress size={14} />
                        ) : (
                          <AutoAwesomeIcon fontSize="small" />
                        )
                      }
                      onClick={handleGenerate}
                      disabled={generating}
                    >
                      Generate using AI
                    </Button>
                  </span>
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  Written for a Director
                </Typography>
              </Stack>
            )}
            {genError && (
              <Typography variant="caption" color="error">
                Couldn't generate narrative. Save the report and try again.
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              minRows={2}
              placeholder="Narrative text…"
              value={b.text}
              onChange={(e) => onChange({ ...b, text: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              minRows={1}
              size="small"
              label="Narration script (optional — spoken in presentation mode)"
              value={b.narration ?? ""}
              onChange={(e) => onChange({ ...b, narration: e.target.value })}
            />
          </Stack>
        );
      }
      case "chart": {
        const b = block as ChartBlock;
        return (
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Caption"
              value={b.title ?? ""}
              onChange={(e) => onChange({ ...b, title: e.target.value })}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Metric"
                value={b.metric}
                onChange={(e) => onChange({ ...b, metric: e.target.value })}
              >
                {METRIC_OPTIONS.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label="Chart type"
                value={b.chartKind}
                onChange={(e) =>
                  onChange({ ...b, chartKind: e.target.value as ChartBlock["chartKind"] })
                }
              >
                {CHART_KIND_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Granularity"
                value={b.granularity}
                onChange={(e) =>
                  onChange({ ...b, granularity: e.target.value as ChartBlock["granularity"] })
                }
              >
                {GRANULARITY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label="Period"
                value={b.windowDays}
                onChange={(e) =>
                  onChange({ ...b, windowDays: Number(e.target.value) })
                }
              >
                {WINDOW_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 1, p: 1 }}>
              <ChartBlockView block={b} clientId={clientId} />
            </Box>
          </Stack>
        );
      }
      case "pageBreak":
        return (
          <Box
            sx={{
              borderTop: "2px dashed",
              borderColor: "divider",
              textAlign: "center",
              color: "text.disabled",
              py: 0.5,
            }}
          >
            <Typography variant="caption">— new presentation page / PDF page —</Typography>
          </Box>
        );
      default:
        break;
    }

    // Generated slide blocks (cover, execSummary, breakdown, kpiCards, aiPanel,
    // actionPlan, correlation, scatter, weatherImpact): editable title + optional
    // key message + a live preview of the rendered slide.
    if (TITLED_GENERATED.has(block.type)) {
      const b = block as ReportBlock & {
        title?: string;
        keyMessage?: string;
      };
      return (
        <Stack spacing={2}>
          <TextField
            fullWidth
            size="small"
            label="Slide title"
            value={b.title ?? ""}
            onChange={(e) =>
              onChange({ ...b, title: e.target.value } as ReportBlock)
            }
          />
          {KEY_MESSAGE_TYPES.has(block.type) && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Key message (shown under the data)"
              value={b.keyMessage ?? ""}
              onChange={(e) =>
                onChange({ ...b, keyMessage: e.target.value } as ReportBlock)
              }
            />
          )}
          {block.type === "actionPlan" && (
            <Stack spacing={1}>
              {onGenerateActionPlan && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Tooltip title="Draft a prioritised action plan for a Director from this report">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          generating ? (
                            <CircularProgress size={14} />
                          ) : (
                            <AutoAwesomeIcon fontSize="small" />
                          )
                        }
                        onClick={async () => {
                          if (!onGenerateActionPlan) return;
                          setGenerating(true);
                          setGenError(false);
                          try {
                            const items = await onGenerateActionPlan();
                            onChange({ ...block, items });
                          } catch {
                            setGenError(true);
                          } finally {
                            setGenerating(false);
                          }
                        }}
                        disabled={generating}
                      >
                        Generate with AI
                      </Button>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    Written for a Director
                  </Typography>
                </Stack>
              )}
              {genError && (
                <Typography variant="caption" color="error">
                  Couldn't generate. Save the report and try again.
                </Typography>
              )}
              {block.items.length > 0 && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  label="Action items (one per line)"
                  value={block.items.join("\n")}
                  onChange={(e) =>
                    onChange({
                      ...block,
                      items: e.target.value
                        .split("\n")
                        .map((l) => l.trim())
                        .filter(Boolean),
                    })
                  }
                />
              )}
            </Stack>
          )}
          {block.type === "scatter" && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="X metric"
                value={block.xMetric}
                onChange={(e) =>
                  onChange({ ...block, xMetric: e.target.value })
                }
              >
                {COMPARISON_METRIC_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label="Y metric"
                value={block.yMetric}
                onChange={(e) =>
                  onChange({ ...block, yMetric: e.target.value })
                }
              >
                {COMPARISON_METRIC_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label="Group by"
                value={block.dimension}
                onChange={(e) =>
                  onChange({
                    ...block,
                    dimension: e.target.value as typeof block.dimension,
                  })
                }
              >
                {DIMENSION_OPTIONS.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
          {block.type === "breakdown" && (
            <TextField
              select
              fullWidth
              size="small"
              label="Group by"
              value={block.dimension}
              onChange={(e) =>
                onChange({
                  ...block,
                  dimension: e.target.value as typeof block.dimension,
                })
              }
            >
              {DIMENSION_OPTIONS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          {block.type === "correlation" && (
            <TextField
              select
              fullWidth
              size="small"
              label="Group by"
              value={block.dimension}
              onChange={(e) =>
                onChange({
                  ...block,
                  dimension: e.target.value as typeof block.dimension,
                })
              }
            >
              {DIMENSION_OPTIONS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Box
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 1,
              p: 1,
              bgcolor: "#0B0F14",
              color: "#fff",
            }}
          >
            <SlideBlockView block={block} clientId={clientId} />
          </Box>
        </Stack>
      );
    }

    return null;
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      {header}
      {renderBody()}
    </Paper>
  );
};
